import {
  Component,
  effect,
  ElementRef,
  input,
  OnDestroy,
  viewChild,
} from '@angular/core';
import { DotLottieWorker } from '@lottiefiles/dotlottie-web';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { environment } from '../../../../environments/environment';

gsap.registerPlugin(SplitText);

@Component({
  selector: 'squadbox-chatbot-is-thinking',
  template: `
    <div class="fade-in flex items-center gap-3">
      <canvas #lottieCanvas class="size-8"></canvas>
      <span #aiThinkingText class="ai-thinking-text text-neutral-500 typo-p2">
        {{ text() }}
      </span>
    </div>
  `,
})
export class ChatbotIsThinkingComponent implements OnDestroy {
  public readonly text = input.required<string>();
  public readonly lottieCanvas =
    viewChild<ElementRef<HTMLCanvasElement>>('lottieCanvas');
  public readonly aiThinkingText =
    viewChild<ElementRef<HTMLSpanElement>>('aiThinkingText');

  private loaderLottieWorker: DotLottieWorker | null = null;
  private loadingLabelAnimation: gsap.core.Timeline | null = null;

  constructor() {
    effect(() => {
      const canvas = this.lottieCanvas();
      const textElement = this.aiThinkingText();

      if (canvas && textElement) {
        this.initializeLoaderAnimation(canvas.nativeElement);
        this.initializeLoadingLabelAnimation(textElement.nativeElement);
      } else {
        this.cleanupLoaderAnimation();
        this.cleanupLoadingLabelAnimation();
      }
    });
  }

  ngOnDestroy(): void {
    this.cleanupLoaderAnimation();
    this.cleanupLoadingLabelAnimation();
  }

  private initializeLoaderAnimation(canvas: HTMLCanvasElement): void {
    if (this.loaderLottieWorker) {
      return; // Already initialized
    }

    try {
      this.loaderLottieWorker = new DotLottieWorker({
        canvas,
        src: `${environment.appUiUrl}/assets/lottie/ai-thinking.lottie`,
        autoplay: true,
        loop: true,
      });
    } catch (error) {
      console.error('Failed to initialize Lottie animation:', error);
    }
  }

  private cleanupLoaderAnimation(): void {
    if (this.loaderLottieWorker) {
      void this.loaderLottieWorker.destroy();
      this.loaderLottieWorker = null;
    }
  }

  private initializeLoadingLabelAnimation(textElement: HTMLSpanElement): void {
    if (this.loadingLabelAnimation) {
      return; // Already initialized
    }

    // Wait for the next tick to ensure the text is rendered/translated
    setTimeout(() => {
      // Check if element still exists and has content
      if (!textElement?.textContent?.trim()) {
        return;
      }

      try {
        const split = new SplitText(textElement, { type: 'chars' });
        const chars = split.chars;

        // Ensure we have characters to animate
        if (!chars || chars.length === 0) {
          return;
        }

        const segmentLength = 2;
        const duration = 0.6;
        const delay = 0.3;

        const segmentStagger = (i: number) =>
          Math.floor(i / segmentLength) * delay; // add the delay between segments

        this.loadingLabelAnimation = gsap.timeline();
        this.loadingLabelAnimation.to(chars, {
          keyframes: [
            { color: '#313850', duration, ease: 'power2.inOut' }, // to neutral-700
            { color: '#787c8f', duration, ease: 'power2.inOut' }, // back to neutral-500
          ],
          stagger: segmentStagger,
          repeat: -1,
          repeatDelay: -delay,
        });
      } catch (error) {
        console.error('Failed to initialize text animation:', error);
      }
    }, 0);
  }

  private cleanupLoadingLabelAnimation(): void {
    if (this.loadingLabelAnimation) {
      this.loadingLabelAnimation.kill();
      this.loadingLabelAnimation = null;
    }
  }
}
